import { memo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { Button } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { GaErrorBanner } from "app/modules/ga-connect/GaErrorBanner";
import ActivateConnectionButton from "app/pages/activation/components/GAWidget/ActivateConnectionButton/ActivateConnectionButton";
import { ActivationModal } from "app/pages/activation/components/GAWidget/ActivationModal/ActivationModal";
import { ConnectionInfo } from "app/pages/activation/components/GAWidget/ConnectionInfo/ConnectionInfo";
import { gaWidgetDataAutomation } from "app/pages/activation/components/GAWidget/dataAutomationKeys";
import SortableWidget from "app/pages/activation/components/GAWidget/DraggableWidget/SortableWidget";
import * as SC from "app/pages/activation/modules/Project/styles";
import { useUpdateTrafficSourceMutation } from "app/pages/activation/queries/data/useUpdateTrafficSourceMutation";
import { BenchmarkTabs } from "app/pages/benchmark-tracker";
import { BENCHMARK_TRACKER_REPORT_ROUTE } from "app/pages/benchmark-tracker";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import useGAWidgetData from "../../hooks/helpers/useGAWidgetData";
import { useGAWidgetOrder } from "../../hooks/home/useGAWidgetOrder";
import { activationTracking } from "../../services/tracking";
import type { FirstPartyDataSource } from "../../types/api";
import { useActivationGaMappingState, GaMappingWarningBanner } from "../ga-connect";
import { Collapse } from "../Shared/Collapse/Collapse";

import { ConnectGaBtn } from "./ConnectGaBtn/ConnectGaBtn";
import { SortableCarousel } from "./DraggableWidget/SortableCarousel";
import { CarouselWrapper, ConnectGAContainer, GAContainer, WidgetsContainer } from "./styles";
import { GAWidgetTexts } from "./texts";
import { GA_SOURCE, SW_SOURCE, getWidgetsConfig } from "./widgetsConfig";

type GAWidgetSchema = {
    activationId?: string;
    granularity: GranularityNames;
    duration: string;
    isDataLoading: boolean;
    domain: string;
    source?: FirstPartyDataSource;
    defaultDuration: string;
    isViewerMode?: boolean;
    benchmarkId?: string;
    setSource: (source: FirstPartyDataSource) => void;
};

export const GAWidget = memo(
    ({
        activationId,
        granularity,
        duration,
        isDataLoading,
        source,
        isViewerMode = false,
        benchmarkId,
        defaultDuration,
        setSource,
    }: GAWidgetSchema) => {
        const track = activationTracking.home;
        const { widgetOrder: metrics, onDragEnd } = useGAWidgetOrder();

        const [isWidgetHover, setIsWidgetHover] = useState(false);

        const {
            params: { country, timeGranularity, webSource },
        } = useCurrentStateAndParams();

        const isConnected = !!source;
        const isGaSource = source === GA_SOURCE;
        const isSwSource = source === SW_SOURCE;

        const { hasGaMapping, isMappingStateFaulty, isMappingLoading } = useActivationGaMappingState(activationId, {
            enabled: !!activationId && !!source,
        });

        const isMappingValidated = isGaSource ? !isMappingLoading && !isMappingStateFaulty : true;
        const shouldDisplayGAWidgetData = isConnected && isMappingValidated;
        const { metricData, isLoading, isError, gaErrorKind, refetch } = useGAWidgetData(
            activationId,
            granularity,
            shouldDisplayGAWidgetData,
            source,
        );

        const { mutate: updateTrafficSource } = useUpdateTrafficSourceMutation(activationId);

        const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);

        const isMappingLoaded = isGaSource ? !isMappingLoading : true;
        const isWidgetDataLoading = !isMappingLoaded || isLoading;
        const shouldRenderConnectContainer = !isDataLoading && isMappingLoaded && !shouldDisplayGAWidgetData;

        const onActivationModalOpen = () => {
            setIsActivationModalOpen(true);
            track.trackMainMetricsButton();
        };

        const onActivationModalClose = () => {
            setIsActivationModalOpen(false);
            track.trackMainMetricsCloseModal();
        };

        const onGAModalOpen = () => {
            onActivationModalClose();
            track.trackGAConnectButton("Activate 1st Party Data");
        };

        const onSWConnect = () => {
            onActivationModalClose();

            setSource(SW_SOURCE);
            updateTrafficSource(SW_SOURCE);

            track.trackSWButton();
        };

        const goToBenchmark = async () => {
            if (isConnected) {
                await proNavigator.go(BENCHMARK_TRACKER_REPORT_ROUTE, {
                    key: benchmarkId,
                    country,
                    granularity: timeGranularity,
                    webSource,
                    duration: defaultDuration,
                    tab: BenchmarkTabs.engagement,
                });
            }
        };

        const onWidgetClick = () => {
            track.trackUserClickTrafficEngagementWidget();
            void goToBenchmark();
        };

        const onTitleClick = () => {
            track.trackUserClickTrafficEngagementTitle();
            void goToBenchmark();
        };

        const onCTAClick = () => {
            track.trackUserClickTrafficEngagementCTA();
            void goToBenchmark();
        };

        const onToggle = (isOpen: boolean) => {
            track.trackToggleCollapse(isOpen ? "collapse" : "expand");
        };

        const isConnectedAndNoError = shouldDisplayGAWidgetData && !isError;

        return (
            <>
                <ActivationModal
                    activationId={activationId}
                    hasGaMapping={hasGaMapping}
                    isOpen={isActivationModalOpen}
                    onClose={onActivationModalClose}
                    onGAConnect={onGAModalOpen}
                    onSWConnect={onSWConnect}
                />

                {isError && isGaSource && gaErrorKind ? (
                    <Box
                        paddingY="24px 0"
                        width="100%"
                    >
                        <GaErrorBanner
                            errorKind={gaErrorKind}
                            onReload={refetch}
                        />
                    </Box>
                ) : null}

                {activationId && source ? (
                    <GaMappingWarningBanner
                        activationId={activationId}
                        dataAutomationPrefix={gaWidgetDataAutomation.gaMappingInfoBannerPrefix}
                        isGaSource={isGaSource}
                        withTopPadding
                    />
                ) : null}

                <GAContainer>
                    <Collapse
                        defaultOpen
                        onToggle={onToggle}
                        contentClassName={"contentWithShadow"}
                        title={
                            <SC.WidgetHeader>
                                <SC.WidgetTitle
                                    isTitleLink={isConnected}
                                    isWidgetHover={isWidgetHover}
                                    onClick={onTitleClick}
                                >
                                    {translate(GAWidgetTexts.title)}
                                </SC.WidgetTitle>

                                {!isDataLoading && !isLoading && activationId && isConnected ? (
                                    <ConnectionInfo
                                        activationId={activationId}
                                        duration={duration}
                                        source={source}
                                    />
                                ) : null}
                            </SC.WidgetHeader>
                        }
                        rightActionBlock={
                            isDataLoading || isGaSource || isViewerMode || !activationId ? null : (
                                <ConnectGaBtn activationId={activationId} />
                            )
                        }
                    >
                        <WidgetsContainer>
                            <CarouselWrapper
                                onMouseEnter={() => setIsWidgetHover(true)}
                                onMouseLeave={() => setIsWidgetHover(false)}
                            >
                                <SortableCarousel
                                    items={metrics}
                                    onDragEnd={onDragEnd}
                                    dataAutomation={gaWidgetDataAutomation.carouselListWidgets}
                                    data={metricData}
                                    granularity={timeGranularity}
                                    isSwSource={isSwSource}
                                    forceHover
                                >
                                    {metrics.map((metricName: string, index) => (
                                        <SortableWidget
                                            key={metricName}
                                            index={index}
                                            id={metricName}
                                            metricName={metricName}
                                            isConnected={isConnectedAndNoError}
                                            isSwSource={isSwSource}
                                            isLoading={isWidgetDataLoading || isDataLoading}
                                            data={metricData?.[metricName]}
                                            granularity={timeGranularity}
                                            withBorder={index !== metrics.length - 1}
                                            onClick={onWidgetClick}
                                            {...getWidgetsConfig()[metricName]}
                                        />
                                    ))}
                                </SortableCarousel>
                            </CarouselWrapper>
                            {isConnectedAndNoError ? (
                                <SC.GAFooter>
                                    <Button
                                        onClick={onCTAClick}
                                        type="flat"
                                    >
                                        {translate(GAWidgetTexts.goToBenchmark)}
                                    </Button>
                                </SC.GAFooter>
                            ) : null}

                            {shouldRenderConnectContainer ? (
                                <ConnectGAContainer>
                                    <ActivateConnectionButton
                                        onClick={onActivationModalOpen}
                                        isViewerMode={isViewerMode}
                                    />
                                </ConnectGAContainer>
                            ) : null}
                        </WidgetsContainer>
                    </Collapse>
                </GAContainer>
            </>
        );
    },
);

GAWidget.displayName = "GAWidget";
